import { useCallback, useEffect, useState, useRef } from 'react';
import type { CascadingSelectState } from '@atlassian/jira-jql-builder-basic-cascading-select/src/common/types.tsx';
import type { useHydrationQueryLoaderQuery$data } from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/__generated__/useHydrationQueryLoaderQuery.graphql';
import type { JQLBuilderUI$data } from '@atlassian/jira-jql-builder/src/ui/__generated__/JQLBuilderUI.graphql';
import type { AdvancedContent$data } from '@atlassian/jira-filter-refinement/src/ui/filter-popup/filter-popup-content/ui/advanced/__generated__/AdvancedContent.graphql';

export const useSelectedValues = (
	hydratedData: useHydrationQueryLoaderQuery$data | JQLBuilderUI$data | AdvancedContent$data,
	selectedOptionsFromHydration: CascadingSelectState | undefined,
) => {
	const prevHydrationData = useRef<
		useHydrationQueryLoaderQuery$data | JQLBuilderUI$data | AdvancedContent$data
	>(hydratedData);
	const [hydrated, setHydrated] = useState(true);

	// Allow optimistic updates when a value is selected while hydration is taking place
	const [optimisticValues, setOptimisticValues] = useState<CascadingSelectState | undefined>();

	// Only flag values as hydrated after a data change
	useEffect(() => {
		if (!hydrated && hydratedData !== prevHydrationData.current) {
			setHydrated(true);
		}
		prevHydrationData.current = hydratedData;
	}, [hydrated, hydratedData]);

	const setSelectedValues = useCallback((values: CascadingSelectState) => {
		setHydrated(false);
		setOptimisticValues(values);
	}, []);

	const selectedValues =
		!hydrated && optimisticValues ? optimisticValues : selectedOptionsFromHydration;
	return [selectedValues, setSelectedValues] as const;
};
